/*
 * Copyright (C) Siemens AG 2026. All rights reserved. Confidential
 */

import { createAction, props } from '@ngrx/store';
import { Announcement } from 'src/app/model/announcement.model';
import { AuthPortalUser, TenantUser } from 'src/app/model/platformuser.model';
import { PricingPlan } from 'src/app/model/pricingplan';
import { ProjectExternal } from 'src/app/model/project.external';
import { SIMITFileUploadHistoryItem } from 'src/app/model/simitfileuploadhistory.model';
import { SubTenant } from 'src/app/model/subtenant';

export const LoadIdentity = createAction(
    '[CoreAppFlow] Initialize Core Data',
    props<{payload: any}>()
)

export const IdentityFailure = createAction(
    '[Identity] Identity validation failure',
    props<{payload: undefined}>()
)

export const IdentitySuccess = createAction(
    '[Identity] Identity validation success',
    props<{payload: any}>()
)

export const LoadPortalAuthUser = createAction(
    '[AuthUserState] Initialize Auth User state',
    props<{payload: undefined}>()
)

export const RefreshPortalAuthUser = createAction(
    '[AuthUserState] Refresh Auth User state',
    props<{payload: undefined}>()
)

export const AuthPortalUserSuccess = createAction(
    '[AuthUserState] Validate Auth User state',
    props<{payload: {authUser: AuthPortalUser, currentTenantId: null | number}}>()
)

export const TenantIdChanged = createAction(
    '[CurrentTenantData] Tenant ID changed - reset all tenant data',
    props<{payload: {previousTenantId: number | null, newTenantId: number | null}}>()
)

export const AuthPortalUserFailure = createAction(
    '[AuthUserState] Invalidate Auth User state',
    props<{payload: undefined}>()
)

export const LoadAvailableNeoSimitVersions = createAction(
    '[NeoSimitVersions] Load Available Neo and Simit Versions',
    props<{payload: undefined}>()
)

export const SuccessAvailableNeoSimitVersions = createAction(
    '[NeoSimitVersions] Success on Load Available Neo and Simit Versions',
    props<{payload: string[]}>()
)

export const FailAvailableNeoSimitVersions = createAction(
    '[NeoSimitVersions] Fail on Load Available Neo and Simit Versions',
    props<{payload: undefined}>()
)

export const LoadCurrentTenantProjects = createAction(
    '[CurrentTenantData] Load current tenant projects',
    props<{payload: undefined}>()
)

export const RefreshCurrentTenantProjects = createAction(
    '[CurrentTenantData] Refresh current tenant projects',
    props<{payload: undefined}>()
)

export const SuccessCurrentTenantProjects = createAction(
    '[CurrentTenantData] Load current tenant projects success',
    props<{payload: ProjectExternal[]}>()
)

export const FailureCurrentTenantProjects = createAction(
    '[CurrentTenantData] Load current tenant projects failure',
    props<{payload: undefined}>()
)

export const LoadCurrentTenantUsers = createAction(
    '[CurrentTenantData] Load current tenant users',
    props<{payload: undefined}>()
)

export const SuccessCurrentTenantUsers = createAction(
    '[CurrentTenantData] Load current tenant users success',
    props<{payload: TenantUser[]}>()
)

export const FailureCurrentTenantUsers = createAction(
    '[CurrentTenantData] Load current tenant users failure',
    props<{payload: undefined}>()
)

export const LoadCurrentTenantSubTenants = createAction(
    '[CurrentTenantData] Load current tenant subtenants',
    props<{payload: undefined}>()
)   

export const SuccessCurrentTenantSubTenants = createAction(
    '[CurrentTenantData] Load current tenant subtenants success',
    props<{payload: SubTenant[]}>()
)   

export const FailureCurrentTenantSubTenants = createAction(
    '[CurrentTenantData] Load current tenant subtenants failure',
    props<{payload: undefined}>()
)   

export const LoadCurrentTenantPricingPlans = createAction(  
    '[CurrentTenantData] Load current tenant pricing plans',  
    props<{payload: undefined}>()  
)   

export const SuccessCurrentTenantPricingPlans = createAction(
    '[CurrentTenantData] Load current tenant pricing plans success',
    props<{payload: PricingPlan[]}>()
)

export const FailureCurrentTenantPricingPlans = createAction(
    '[CurrentTenantData] Load current tenant pricing plans failure',
    props<{payload: undefined}>()
)   

export const LoadAnnouncements = createAction(
    '[Announcements] Load announcements',
    props<{payload: undefined}>()
)

export const SuccessAnnouncements = createAction(
    '[Announcements] Load announcements success',
    props<{payload: Announcement[]}>()
)

export const FailureAnnouncements = createAction(
    '[Announcements] Load announcements failure',
    props<{payload: undefined}>()
)

export const SuccessSingleAnnouncementImage = createAction(
    '[Announcements] Load single announcement image success',
    props<{payload: {imageKey: string, value: string}}>()
)

export const FailureSingleAnnouncementImage = createAction(
    '[Announcements] Load single announcement image failure',
    props<{payload: {imageKey: string}}>()
)

export const LoadCurrentTenantPriceList = createAction(
    '[CurrentTenantData] Load current tenant price list',
    props<{payload: undefined}>()
)

export const SuccessCurrentTenantPriceList = createAction(
    '[CurrentTenantData] Load current tenant price list success',
    props<{payload: any}>()
)

export const FailureCurrentTenantPriceList = createAction(
    '[CurrentTenantData] Load current tenant price list failure',
    props<{payload: undefined}>()
)

export const LoadSIMITFileUploadHistory = createAction(
    '[CurrentTenantData] Load SIMIT file upload history',
    props<{payload: undefined}>()
)

export const SuccessSIMITFileUploadHistory = createAction(
    '[CurrentTenantData] Load SIMIT file upload history success',
    props<{payload: SIMITFileUploadHistoryItem[]}>()
)

export const FailureSIMITFileUploadHistory = createAction(
    '[CurrentTenantData] Load SIMIT file upload history failure',
    props<{payload: undefined}>()
)

